import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../contexts/OrderContext';
import PendingOrderHandler from '../utils/pendingOrderHandler';
import styles from '../styles/RegisterPage.module.css';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Helmet } from 'react-helmet';
import validator from 'validator'; // Import validator.js

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signup } = useAuth();
  const { updateOrderDetails } = useOrder();
  const navigate = useNavigate();
  const location = useLocation();

  const validateForm = () => {
    if (!name.trim()) {
      setError('Name is required');
      return false;
    }

    if (!validator.isEmail(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Added domain check:
    const domain = email.split('@')[1];
    if (domain === 'ail.com') {
      setError('Invalid email domain');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const userCredential = await signup(email, password, name);
      const user = userCredential.user;

      const tempUserId = localStorage.getItem('pendingOrderUserId');

      if (tempUserId) {
        try {
          const pendingOrder = await PendingOrderHandler.getPendingOrder(tempUserId);

          if (pendingOrder) {
            Object.keys(pendingOrder).forEach((key) => {
              if (key !== 'createdAt' && key !== 'id') {
                updateOrderDetails(key, pendingOrder[key]);
              }
            });
            localStorage.removeItem('pendingOrderUserId');
            navigate('/checkout/complete', { state: { fromLogin: true } });
            return;
          }
        } catch (pendingError) {
          console.error('Error fetching pending order:', pendingError);
        }
      }

      const fromCheckout = location.state?.from === '/checkout/complete';
      const hasPendingOrder = location.state?.pendingOrder;

      if (fromCheckout || hasPendingOrder) {
        navigate('/checkout/complete', { state: { fromLogin: true } });
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      console.error('Register Error:', err);

      const errorMessages = {
        'auth/email-already-in-use': 'Email is already registered. Please log in or use a different email.',
        'auth/invalid-email': 'Invalid email address format.',
        'auth/weak-password': 'Password is too weak. Please use a stronger password.',
        default: 'Failed to create an account. ' + err.message,
      };

      setError(errorMessages[err.code] || errorMessages.default);
    } finally {
      setLoading(false);
    }
  };

  const getRedirectPath = () => {
    if (location.state?.from === '/checkout/complete' || location.state?.pendingOrder) {
      return '/checkout/complete';
    }
    return '/dashboard';
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Create Your Account | MakeMeSongs</title>
        <meta name="description" content="Sign up for MakeMeSongs to create personalized custom songs, manage your orders, and access your finished music. Start your musical journey today!" />
        <meta name="keywords" content="custom song account, personalized music signup, create music account, register for custom songs" />
        <meta property="og:title" content="Create Your Account | MakeMeSongs" />
        <meta property="og:description" content="Sign up to create personalized custom songs, manage your orders, and access your finished music." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/register" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com/register" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className={styles.registerBox}>
        <h1 className={styles.heading}>Create Account</h1>
        {error && <div className={styles.error}>{error}</div>}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formControl}>
            <label htmlFor="name" className={styles.formLabel}>Name</label>
            <input
              type="text"
              id="name"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={styles.formControl}>
            <label htmlFor="email" className={styles.formLabel}>Email</label>
            <input
              type="email"
              id="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className={styles.formControl}>
            <label htmlFor="password" className={styles.formLabel}>Password</label>
            <input
              type="password"
              id="password"
              className={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <div className={styles.formControl}>
            <label htmlFor="confirmPassword" className={styles.formLabel}>Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              className={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? 'Loading...' : 'Sign Up'}
          </button>
        </form>

        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: '15px 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
            <p style={{ margin: '0 10px', color: '#666' }}>or</p>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
          </div>
          <GoogleSignInButton redirectPath={getRedirectPath()} />
        </div>

        <div className={styles.links}>
          <p>
            Already have an account?{' '}
            <Link to="/login" className={styles.link}>Log In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;